import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Button,
  Flex,
  useToast,
  Icon,
  Select,
  Spinner,
  Text,
  Divider,
} from "@chakra-ui/react";
import { useState, useEffect, useCallback } from "react";
import { FiBriefcase, FiEdit, FiSearch, FiMapPin } from "react-icons/fi";
import OptionsService from "../ManageBranches/OptionsService";
import CompanyAddressService from "../Enterprise/CompanyAddressService";
import ModernPageShell from "../../components/layout/ModernPageShell";
import { getModernUi } from "../../components/layout/modernUi";

export default function ManageCompanies({ modern = false }) {
  const ui = getModernUi(modern);
  const toast = useToast();

  // Estados para a lista de empresas e seu carregamento
  const [empresasOptions, setEmpresasOptions] = useState([]);
  const [loadingEmpresas, setLoadingEmpresas] = useState(true);
  const [errorEmpresas, setErrorEmpresas] = useState(null);

  // Estados para a empresa selecionada e o formulário de edição
  const [selectedCompanyIdForEdit, setSelectedCompanyIdForEdit] = useState("");
  const [editCompanyForm, setEditCompanyForm] = useState({
    optionsId: null,
    description: "",
  });

  // NOVOS ESTADOS para a lista de TODAS as filiais e seu carregamento
  const [allFiliaisOptions, setAllFiliaisOptions] = useState([]);
  const [loadingAllFiliais, setLoadingAllFiliais] = useState(true); // Inicia como true para carregar ao montar
  const [errorAllFiliais, setErrorAllFiliais] = useState(null);

  // Estados para a filial selecionada e o formulário de edição
  const [selectedFilialIdForEdit, setSelectedFilialIdForEdit] = useState("");
  const [editFilialForm, setEditFilialForm] = useState({
    companyAddressId: null,
    unityName: "",
    floor: "",
    additionalAddressDetails: "",
    additionalAddressDetails2: "",
    company: null,
  });

  // Função para buscar as empresas da API
  const fetchCompanies = useCallback(async () => {
    try {
      setLoadingEmpresas(true);
      setErrorEmpresas(null);
      const data = await OptionsService.getOptionsByType("Company");
      setEmpresasOptions(data);
    } catch (err) {
      setErrorEmpresas("Não foi possível carregar as empresas.");
      toast({
        title: "Erro ao carregar empresas.",
        description:
          "Verifique sua conexão com o backend ou se há empresas cadastradas.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Erro ao carregar empresas:", err);
    } finally {
      setLoadingEmpresas(false);
    }
  }, [toast]);

  // NOVA FUNÇÃO: Buscar TODAS as filiais
  const fetchAllFiliais = useCallback(async () => {
    try {
      setLoadingAllFiliais(true);
      setErrorAllFiliais(null);
      const data = await CompanyAddressService.getAllCompanyAddresses();
      setAllFiliaisOptions(data);
      if (data.length === 0) {
        toast({
          title: "Nenhuma filial encontrada.",
          description: "Não há filiais cadastradas no sistema.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (err) {
      setErrorAllFiliais("Não foi possível carregar as filiais.");
      toast({
        title: "Erro ao carregar filiais.",
        description:
          "Verifique sua conexão com o backend ou se há filiais cadastradas.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Erro ao carregar todas as filiais:", err);
    } finally {
      setLoadingAllFiliais(false);
    }
  }, [toast]);

  // Efeito para carregar as empresas e TODAS as filiais ao montar o componente
  useEffect(() => {
    fetchCompanies();
    fetchAllFiliais(); // Chama a nova função para carregar todas as filiais
  }, [fetchCompanies, fetchAllFiliais]);

  // Handler para quando uma empresa é selecionada no dropdown de edição
  const handleSelectCompanyForEdit = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyIdForEdit(companyId);

    if (companyId) {
      const companyToEdit = empresasOptions.find(
        (emp) => String(emp.optionsId) === String(companyId)
      );
      if (companyToEdit) {
        setEditCompanyForm({
          optionsId: companyToEdit.optionsId,
          description: companyToEdit.description,
        });
      }
    } else {
      setEditCompanyForm({ optionsId: null, description: "" });
      // Limpa a seleção e o formulário da filial se a empresa for desmarcada
      setSelectedFilialIdForEdit("");
      setEditFilialForm({
        companyAddressId: null,
        unityName: "",
        floor: "",
        additionalAddressDetails: "",
        additionalAddressDetails2: "",
        company: null,
      });
    }
  };

  // Handler para quando uma filial é selecionada no dropdown de edição
  const handleSelectFilialForEdit = (e) => {
    const filialId = e.target.value;
    setSelectedFilialIdForEdit(filialId);

    if (filialId) {
      // Busca na lista de TODAS as filiais
      const filialToEdit = allFiliaisOptions.find(
        (filial) => String(filial.companyAddressId) === String(filialId)
      );
      if (filialToEdit) {
        setEditFilialForm({
          companyAddressId: filialToEdit.companyAddressId,
          unityName: filialToEdit.unityName || "",
          floor: filialToEdit.floor || "",
          additionalAddressDetails: filialToEdit.additionalAddressDetails || "",
          additionalAddressDetails2:
            filialToEdit.additionalAddressDetails2 || "",
          company: filialToEdit.company || null,
        });
      }
    } else {
      setEditFilialForm({
        companyAddressId: null,
        unityName: "",
        floor: "",
        additionalAddressDetails: "",
        additionalAddressDetails2: "",
        company: null,
      });
    }
  };

  // Handler para mudanças nos campos do formulário de edição de empresa
  const handleChangeEditCompany = (e) => {
    const { name, value } = e.target;
    setEditCompanyForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handler para mudanças nos campos do formulário de edição de filial
  const handleChangeEditFilial = (e) => {
    const { name, value } = e.target;
    setEditFilialForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handler para submeter o formulário de edição de empresa
  const handleSubmitEditCompany = async () => {
    if (!editCompanyForm.optionsId || !editCompanyForm.description) {
      toast({
        title: "Selecione uma empresa e preencha o nome.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updatedData = {
        optionsId: editCompanyForm.optionsId,
        description: editCompanyForm.description,
        type: "Company",
      };

      await OptionsService.updateOption(updatedData);

      toast({
        title: "Empresa atualizada com sucesso!",
        description: `Nome: ${updatedData.description}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      fetchCompanies();
      setSelectedCompanyIdForEdit("");
      setEditCompanyForm({ optionsId: null, description: "" });

      setSelectedFilialIdForEdit("");
      setEditFilialForm({
        companyAddressId: null,
        unityName: "",
        floor: "",
        additionalAddressDetails: "",
        additionalAddressDetails2: "",
        company: null,
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar empresa.",
        description:
          error.response?.data?.message || "Ocorreu um erro desconhecido.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Erro ao atualizar empresa:", error);
    }
  };

  const handleSubmitEditFilial = async () => {
    if (!editFilialForm.companyAddressId || !editFilialForm.unityName) {
      toast({
        title: "Selecione uma filial e preencha o nome da unidade.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const updatedData = {
        companyAddressId: editFilialForm.companyAddressId,
        unityName: editFilialForm.unityName,
        floor: editFilialForm.floor,
        additionalAddressDetails: editFilialForm.additionalAddressDetails,
        additionalAddressDetails2: editFilialForm.additionalAddressDetails2,
        company: editFilialForm.company,
      };

      await CompanyAddressService.updateCompanyAddress(updatedData);

      toast({
        title: "Filial atualizada com sucesso!",
        description: `Nome da Unidade: ${updatedData.unityName}`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      fetchAllFiliais();
      setSelectedFilialIdForEdit("");
      setEditFilialForm({
        companyAddressId: null,
        unityName: "",
        floor: "",
        additionalAddressDetails: "",
        additionalAddressDetails2: "",
        company: null,
      });
    } catch (error) {
      toast({
        title: "Erro ao atualizar filial.",
        description:
          error.response?.data?.message || "Ocorreu um erro desconhecido.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      console.error("Erro ao atualizar filial:", error);
    }
  };

  const pageContent = (
    <>
      {!modern && (
        <Heading
          mb={6}
          fontSize="2xl"
          color="#C1272D"
          borderBottom="2px solid #625752"
          pb={2}
        >
          Consultar e Editar Empresas e Filiais - SENCA
        </Heading>
      )}

      <Box mb={10} {...ui.card}>
        <Heading
          size="md"
          mb={4}
          color="#3A3A3A"
          display="flex"
          alignItems="center"
          gap={2}
        >
          <Icon as={FiSearch} /> Selecione uma Empresa para Editar
        </Heading>

        <FormControl mb={4}>
          <FormLabel color="#3A3A3A">Empresa:</FormLabel>
          {loadingEmpresas ? (
            <Flex justify="center" align="center" height="50px">
              <Spinner size="sm" color="#C1272D" />
              <Box ml={3}>Carregando empresas...</Box>
            </Flex>
          ) : errorEmpresas ? (
            <Box color="red.500">{errorEmpresas}</Box>
          ) : (
            <Select
              placeholder="Selecione uma empresa"
              value={selectedCompanyIdForEdit}
              onChange={handleSelectCompanyForEdit}
              bg="white"
              borderColor="#625752"
              focusBorderColor="#C1272D"
            >
              {empresasOptions.length === 0 && (
                <option disabled>Nenhuma empresa cadastrada.</option>
              )}
              {empresasOptions.map((emp) => (
                <option key={emp.optionsId} value={emp.optionsId}>
                  {emp.description}
                </option>
              ))}
            </Select>
          )}
        </FormControl>
      </Box>

      {/* Formulário de Edição de Empresa (aparece apenas quando uma empresa é selecionada) */}
      {editCompanyForm.optionsId && (
        <>
          <Divider my={10} borderColor="#625752" />
          <Box>
            <Heading
              size="md"
              mb={4}
              color="#3A3A3A"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Icon as={FiEdit} /> Editando Empresa:{" "}
              {editCompanyForm.description}
            </Heading>
            <Flex direction="column" gap={5}>
              <FormControl isRequired>
                <FormLabel
                  color="#3A3A3A"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Icon as={FiBriefcase} /> Nome da Empresa
                </FormLabel>
                <Input
                  placeholder="Novo nome da empresa"
                  name="description"
                  value={editCompanyForm.description}
                  onChange={handleChangeEditCompany}
                  bg="white"
                  borderColor="#625752"
                  focusBorderColor="#C1272D"
                />
              </FormControl>

              <Flex justify="center" gap={4} mt={3}>
                <Button
                  bg="#C1272D"
                  color="white"
                  _hover={{ bg: "#a81f23" }}
                  px={8}
                  onClick={handleSubmitEditCompany}
                >
                  Salvar Alterações da Empresa
                </Button>
                <Button
                  variant="outline"
                  borderColor="#625752"
                  color="#625752"
                  _hover={{ bg: "#f4f4f4" }}
                  px={8}
                  onClick={() => {
                    setSelectedCompanyIdForEdit("");
                    setEditCompanyForm({ optionsId: null, description: "" });
                    setSelectedFilialIdForEdit("");
                    setEditFilialForm({
                      companyAddressId: null,
                      unityName: "",
                      floor: "",
                      additionalAddressDetails: "",
                      additionalAddressDetails2: "",
                      company: null,
                    });
                  }}
                >
                  Cancelar Edição da Empresa
                </Button>
              </Flex>
            </Flex>
          </Box>
        </>
      )}

      {/* Nova Seção: Seleção e Edição de Filial (SEMPRE visível ou quando não está editando empresa) */}
      <>
        <Divider my={10} borderColor="#625752" />
        <Box>
          <Heading
            size="md"
            mb={4}
            color="#3A3A3A"
            display="flex"
            alignItems="center"
            gap={2}
          >
            <Icon as={FiSearch} /> Selecione uma Filial para Editar
          </Heading>

          <FormControl mb={4}>
            <FormLabel color="#3A3A3A">Filial:</FormLabel>
            {loadingAllFiliais ? (
              <Flex justify="center" align="center" height="50px">
                <Spinner size="sm" color="#C1272D" />
                <Box ml={3}>Carregando filiais...</Box>
              </Flex>
            ) : errorAllFiliais ? (
              <Box color="red.500">{errorAllFiliais}</Box>
            ) : (
              <Select
                placeholder="Selecione uma filial"
                value={selectedFilialIdForEdit}
                onChange={handleSelectFilialForEdit}
                bg="white"
                borderColor="#625752"
                focusBorderColor="#C1272D"
              >
                {allFiliaisOptions.length === 0 && (
                  <option disabled>Nenhuma filial cadastrada.</option>
                )}
                {allFiliaisOptions.map((filial) => (
                  <option
                    key={filial.companyAddressId}
                    value={filial.companyAddressId}
                  >
                    {filial.unityName} (Empresa:{" "}
                    {filial.company?.description || "N/A"})
                  </option>
                ))}
              </Select>
            )}
          </FormControl>
        </Box>
      </>

      {/* Formulário de Edição de Filial (aparece apenas quando uma filial é selecionada) */}
      {editFilialForm.companyAddressId && (
        <>
          <Divider my={10} borderColor="#625752" />
          <Box>
            <Heading
              size="md"
              mb={4}
              color="#3A3A3A"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <Icon as={FiEdit} /> Editando Filial: {editFilialForm.unityName}
            </Heading>
            <Flex direction="column" gap={5}>
              <FormControl isRequired>
                <FormLabel
                  color="#3A3A3A"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  <Icon as={FiMapPin} /> Nome da Unidade
                </FormLabel>
                <Input
                  placeholder="Nome da unidade/filial"
                  name="unityName"
                  value={editFilialForm.unityName}
                  onChange={handleChangeEditFilial}
                  bg="white"
                  borderColor="#625752"
                  focusBorderColor="#C1272D"
                />
              </FormControl>

              <FormControl>
                <FormLabel color="#3A3A3A">Detalhes do Endereço 1</FormLabel>
                <Input
                  placeholder="Detalhes adicionais do endereço (opcional)"
                  name="additionalAddressDetails"
                  value={editFilialForm.additionalAddressDetails}
                  onChange={handleChangeEditFilial}
                  bg="white"
                  borderColor="#625752"
                  focusBorderColor="#C1272D"
                />
              </FormControl>

              <Flex justify="center" gap={4} mt={3}>
                <Button
                  bg="#C1272D"
                  color="white"
                  _hover={{ bg: "#a81f23" }}
                  px={8}
                  onClick={handleSubmitEditFilial}
                >
                  Salvar Alterações da Filial
                </Button>
                <Button
                  variant="outline"
                  borderColor="#625752"
                  color="#625752"
                  _hover={{ bg: "#f4f4f4" }}
                  px={8}
                  onClick={() => {
                    setSelectedFilialIdForEdit("");
                    setEditFilialForm({
                      companyAddressId: null,
                      unityName: "",
                      floor: "",
                      additionalAddressDetails: "",
                      additionalAddressDetails2: "",
                      company: null,
                    });
                  }}
                >
                  Cancelar Edição da Filial
                </Button>
              </Flex>
            </Flex>
          </Box>
        </>
      )}
    </>
  );

  if (modern) {
    return (
      <ModernPageShell
        title="Consulta de empresas"
        subtitle="Edite empresas e filiais já cadastradas no sistema."
        eyebrow="Empresas"
        centered
      >
        {pageContent}
      </ModernPageShell>
    );
  }

  return (
    <Box
      maxW="700px"
      mx="auto"
      mt={10}
      p={10}
      bg="white"
      borderRadius="2xl"
      boxShadow="lg"
      border="1px solid"
      borderColor="#DDD"
    >
      {pageContent}
    </Box>
  );
}
